import React, { useState } from "react";
import DatePicker from "react-datepicker";

import CollapsibleMenu from "src/components/common/CollapsibleMenu";
import RadioButtons from "src/components/common/RadioButton";
import * as Validators from "src/utils/validators";

import styles from "./styles.module.scss";
import { useSelector } from "react-redux";
import { SettingsSelectors } from "src/redux/Settings/selectors";

const MATURITY_TYPES = {
    INDEFINITE: "INDEFINITE",
    ONE_YEAR: "ONE_YEAR",
    CUSTOM: "CUSTOM",
    MAX: "MAX",
};

const INDEFINITE_DATE = "9999/12/31";

const MATURITY_OPTIONS = [
    { label: INDEFINITE_DATE, value: MATURITY_TYPES.INDEFINITE },
    { label: "1 Year", value: MATURITY_TYPES.ONE_YEAR },
    { label: "Custom", value: MATURITY_TYPES.CUSTOM },
];

const MAX_MATURITY_OPTIONS = [
    { label: "Max", value: MATURITY_TYPES.MAX },
    { label: "Custom", value: MATURITY_TYPES.CUSTOM },
];

const INDEFINITE_MATURITY = {
    type: MATURITY_TYPES.INDEFINITE,
    date: new Date(INDEFINITE_DATE),
    term: "",
};

const MAX_MATURITY = { type: MATURITY_TYPES.MAX, date: null, term: "" };

const ONE_YEAR_MATURITY = {
    type: MATURITY_TYPES.ONE_YEAR,
    date: null,
    term: 364,
};

const Maturity = ({ onChange }) => {
    const { "enable-90-day-max-maturity-date": enable90DayMaxMaturity } =
        useSelector(SettingsSelectors.FeatureFlags);

    const [maturity, setMaturity] = useState(
        enable90DayMaxMaturity ? MAX_MATURITY : INDEFINITE_MATURITY
    );

    const handleDatePicked = (input, e) => {
        // NOTE: stop auto completing incomplete date string
        if (e.target.value && e.target.value.replace(/[/-]/g, "").length < 8)
            return;

        if (Validators.convertDateToStr(input) === INDEFINITE_DATE) {
            setMaturity(INDEFINITE_MATURITY);
        } else {
            setMaturity({ type: MATURITY_TYPES.CUSTOM, date: input, term: "" });
        }
    };

    const handleTypeChosen = (value) => {
        if (value === MATURITY_TYPES.CUSTOM) {
            let defaultDate = new Date();
            defaultDate.setDate(defaultDate.getDate() + 2);
            setMaturity({ type: value, date: defaultDate, term: "" });
        } else if (value === MATURITY_TYPES.ONE_YEAR) {
            setMaturity(ONE_YEAR_MATURITY);
        } else if (value === MATURITY_TYPES.MAX) {
            setMaturity(MAX_MATURITY);
        } else {
            setMaturity(INDEFINITE_MATURITY);
        }
    };

    const applyMaturity = () => {
        // add new property type for onChange args, it won't affect anything;
        if (maturity.type === MATURITY_TYPES.ONE_YEAR) {
            onChange({ term: 364, type: maturity.type });
        } else {
            onChange({ date: maturity.date, type: maturity.type });
        }
    };

    const isSelectMax = maturity.type === MATURITY_TYPES.MAX;

    return (
        <CollapsibleMenu title="Maturity" dataTestId="tx-preview-maturity">
            <Section className={styles.triplet}>
                {enable90DayMaxMaturity ? (
                    <RadioButtons
                        dataTestId="tx-create-maturity-type"
                        options={MAX_MATURITY_OPTIONS}
                        value={maturity.type}
                        setter={handleTypeChosen}
                    />
                ) : (
                    <RadioButtons
                        options={MATURITY_OPTIONS}
                        dataTestId="tx-create-maturity-type"
                        value={maturity.type}
                        setter={handleTypeChosen}
                    />
                )}
            </Section>

            <Section className={styles.termSelector}>
                <DatePicker
                    className={styles.datePicker}
                    selected={isSelectMax ? null : maturity.date}
                    onChange={handleDatePicked}
                    dateFormat={"yyyy/MM/dd"}
                    minDate={new Date()}
                    onFocus={(e) => e.target.select()} // to make test easier
                    allowSameDay
                    disabled={
                        maturity.type === MATURITY_TYPES.INDEFINITE ||
                        isSelectMax
                    }
                />
                <button data-testid="apply-maturity" onClick={applyMaturity}>
                    Apply
                </button>
            </Section>
        </CollapsibleMenu>
    );
};

const Section = ({ className, children }) => (
    <div className={[styles.section].concat(className || []).join(" ")}>
        <div className={styles.input}>{children}</div>
    </div>
);

export default Maturity;
